import React, { useState } from "react";
import { Sparkles, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";

const STATEMENTS = [
  { key: "reviewed_prospectus", text: "I have reviewed the Founding Grower Prospectus." },
  { key: "understood_model", text: "I understand the Bloom Club operating model." },
  { key: "understood_revenue", text: "I understand the revenue structure (60/40 Year 1, 65/35 Year 2+, fortnightly payments)." },
  { key: "understood_legal", text: "I understand that legal documents will be reviewed and signed in Stage 3." },
  { key: "wish_to_proceed", text: "I wish to proceed to Stage 3 and apply to become a Founding Grower." },
];

export default function Acknowledgement({ locked, completed, onComplete }) {
  const navigate = useNavigate();
  const [checks, setChecks] = useState(Object.fromEntries(STATEMENTS.map((s) => [s.key, false])));
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const allChecked = STATEMENTS.every((s) => checks[s.key]);
  const ready = allChecked && name.trim().length >= 2;

  const submit = async () => {
    setErr("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/acknowledgement/submit", {
        ...checks,
        full_legal_name: name.trim(),
      });
      onComplete?.(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not submit acknowledgement.");
    } finally {
      setSubmitting(false);
    }
  };

  if (locked) {
    return (
      <div className="bloom-card p-10 text-center bloom-locked" data-testid="acknowledgement-locked">
        <Lock className="mx-auto mb-3 text-[var(--bloom-ink-muted)]" strokeWidth={1.5} />
        <h3 className="font-serif text-2xl mb-2">Final Acknowledgement</h3>
        <p className="text-sm text-[var(--bloom-ink-soft)]">Pass the knowledge check at 100% to unlock.</p>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="bloom-card p-10 text-center" data-testid="acknowledgement-complete">
        <Sparkles className="mx-auto mb-3 text-[var(--bloom-brand)]" strokeWidth={1.5} />
        <h3 className="font-serif text-3xl mb-2">Stage 3 Unlocked</h3>
        <p className="font-serif italic text-lg text-[var(--bloom-ink-soft)] max-w-md mx-auto mb-8">
          Beautifully done. Your acknowledgement has been recorded. You may now proceed
          to registration and contract signing.
        </p>
        <button onClick={() => navigate("/application")} className="bloom-btn bloom-btn-primary" data-testid="stage3-continue-btn">
          Continue to Stage 3 →
        </button>
        <div className="mt-6 text-[11px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)]">
          (Begin your Founding Grower application)
        </div>
      </div>
    );
  }

  return (
    <div className="bloom-card p-8 md:p-10" data-testid="acknowledgement-form">
      <div className="flex items-center gap-3 mb-3">
        <Sparkles className="text-[var(--bloom-brand)]" strokeWidth={1.5} />
        <h3 className="font-serif text-2xl">Final Acknowledgement</h3>
      </div>
      <p className="text-sm text-[var(--bloom-ink-soft)] mb-10 max-w-xl">
        A final declaration. Tick each statement, sign your full legal name, and unlock
        Stage 3, where you will complete registration and sign the binding agreements.
      </p>

      <div className="space-y-4">
        {STATEMENTS.map((s, i) => (
          <label key={s.key} className="flex items-start gap-4 p-4 border border-[var(--bloom-border)] cursor-pointer hover:bg-[var(--bloom-surface)] transition" data-testid={`ack-statement-${i + 1}`}>
            <input
              type="checkbox"
              className="bloom-check mt-1"
              checked={checks[s.key]}
              onChange={(e) => setChecks({ ...checks, [s.key]: e.target.checked })}
              data-testid={`ack-check-${s.key}`}
            />
            <span className="text-sm leading-relaxed">{s.text}</span>
          </label>
        ))}
      </div>

      <div className="mt-10">
        <label className="bloom-label block mb-2">Sign your full legal name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Eleanor Rose Whitaker"
          className="bloom-input font-serif italic text-2xl"
          data-testid="ack-signature-input"
        />
        <p className="text-[11px] text-[var(--bloom-ink-muted)] mt-2 italic font-serif">
          By typing your name you confirm the above declarations are true.
          Timestamp will be recorded automatically.
        </p>
      </div>

      {err && <div className="mt-6 text-sm text-[var(--bloom-accent)] font-serif italic">{err}</div>}

      <div className="mt-8 flex justify-end">
        <button
          disabled={!ready || submitting}
          onClick={submit}
          className="bloom-btn bloom-btn-primary"
          data-testid="ack-submit-btn"
        >
          {submitting ? "Submitting…" : "Sign & Unlock Stage 3"}
        </button>
      </div>
    </div>
  );
}
